import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Instagram, ArrowLeft } from "lucide-react";
import BioPageRenderer from "../components/bio/BioPageRenderer";
import { openExternalUrl, normalizeExternalUrl } from "../lib/url-utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PublicBioPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Email capture state
  const [leadEmail, setLeadEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState(""); // "" | "sending" | "success" | "error"
  const [leadEmailError, setLeadEmailError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/bio/${slug}`);
        setPage(response.data);
      } catch {
        setError("Page not found");
      }
      setLoading(false);
    })();
  }, [slug]);

  const trackClick = async (linkId, linkTitle) => {
    try {
      await axios.post(`${BACKEND_URL}/api/bio/${slug}/click-detailed`, { link_id: linkId, link_title: linkTitle || "" });
    } catch { /* no-op */ }
  };

  const handleGridItemClick = (item) => {
    trackClick(item.id, item.caption);
    if (item.link_url) openExternalUrl(item.link_url);
  };
  const handleButtonClick = (link) => {
    trackClick(link.id, link.title);
    if (link.url) openExternalUrl(link.url);
  };
  /** An Instagram post or reel tile.
   *
   *  Tracked through the SAME click ledger the links and grid tiles use,
   *  so embed taps land in the existing link-in-bio analytics rather than
   *  a parallel one nobody reads. The label is the URL because an embed
   *  has no title of its own. */
  const handleEmbedClick = (emb) => {
    trackClick(emb.id, emb.embed_url);
    if (emb.embed_url) openExternalUrl(emb.embed_url);
  };
  /** Buy now on a product card.
   *
   *  The renderer has always accepted `onProductClick`, and NOBODY passed
   *  it — so the button rendered, looked live, and did nothing on every
   *  shop page. Tracked through the SAME click ledger the link buttons
   *  use, so product clicks land in the existing analytics rather than a
   *  parallel one.
   */
  const handleProductClick = (product, link) => {
    trackClick(`product-${product?.id}`, product?.title || "Product");
    if (link?.url) openExternalUrl(link.url);
  };
  const handleSocialClick = (platform, url) => {
    trackClick(`social-${platform}`, platform);
    if (!url) return;
    if (platform === "whatsapp") {
      // Strip any non-numeric except leading + → wa.me/NUMBER
      const digits = url.replace(/[^0-9+]/g, "");
      openExternalUrl(`https://wa.me/${digits.replace(/^\+/, "")}`);
      return;
    }
    if (platform === "email") {
      openExternalUrl(`mailto:${url}`);
      return;
    }
    openExternalUrl(url);
  };

  const submitLead = async () => {
    if (!leadEmail || !page?.id) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadEmail)) {
      setLeadEmailError("Please enter a valid email address");
      return;
    }
    setLeadEmailError("");
    setLeadStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/bio/lead`, { page_id: page.id, email: leadEmail });
      setLeadStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) setLeadEmailError("Too many requests. Please wait a moment.");
      else if (err.response?.status === 400) setLeadEmailError(detail || "Invalid email address");
      else setLeadEmailError("Something went wrong. Please try again.");
      setLeadStatus("error");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-pulse text-center">
        <div className="w-24 h-24 rounded-full bg-gray-200 mx-auto mb-4" />
        <div className="w-32 h-4 bg-gray-200 rounded mx-auto mb-2" />
        <div className="w-24 h-3 bg-gray-200 rounded mx-auto" />
      </div>
    </div>
  );

  if (error || !page) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-tr from-[#FFAE00] via-[#FF0F7B] to-[#682AE9] flex items-center justify-center">
          <Instagram className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
        <p className="text-gray-500 mb-6">This bio page doesn't exist or has been removed.</p>
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors">
          <ArrowLeft className="w-4 h-4" />Go Home
        </button>
      </div>
    </div>
  );

  return (
    <BioPageRenderer
      page={page}
      isPreview={false}
      leadState={{
        email: leadEmail,
        status: leadStatus,
        error: leadEmailError,
        onChange: (v) => { setLeadEmail(v); setLeadEmailError(""); },
        onSubmit: submitLead,
      }}
      handlers={{
        onSocialClick: handleSocialClick,
        onButtonClick: handleButtonClick,
        onGridClick: handleGridItemClick,
        onProductClick: handleProductClick,
        onEmbedClick: handleEmbedClick,
      }}
    />
  );
}
